<script setup lang="ts">
import { SellerSizeGuideQuery } from '@gql/SellerSizeGuideQuery'

const t2 = useT2({
  'Body size': 'Kūno matmenys',
  'Product size': 'Prekės matmenys',
  'How to measure?': 'Kaip matuoti?',
  Measurements: 'Matmenys',
})

const props = defineProps<{
  guideProductId: string | None
  guideBodyId: string | None
  sellerId: string | None
}>()

const KEY_HOW_TO_MEASURE = 'a:how-to-measure'

function getSate(): 'none' | 'body' | 'product' {
  return props.guideProductId ? 'product' : props.guideBodyId ? 'body' : 'none'
}

const data = shallowReactive({
  activeSize: KEY_HOW_TO_MEASURE as string,
  activeUnit: undefined as string | undefined,
  state: getSate(),
})

onMounted(() => (data.state = getSate()))

const guideIdId = computed((): string | undefined => {
  const id = data.state === 'body' ? props.guideBodyId : data.state === 'product' ? props.guideProductId : undefined
  return id || props.guideBodyId || props.guideProductId || undefined
})

const { ui: sizeGuide } = useApolloWrap2<gen.SellerSizeGuideQuery, gen.SellerSizeGuideQueryVariables>(SellerSizeGuideQuery)({
  vars() {
    const guideId = guideIdId.value
    const sellerId = props.sellerId
    if (!guideId || !sellerId) return
    return { seller: { _id: sellerId }, guideId }
  },
  ui: (i) => i?.publicSizeGuide,
})

const unitKey = computed(() => data.activeUnit || sizeGuide.value?.units?.at(0)?.key)
const howToMeasure = computed((): boolean => !!sizeGuide.value?.areas?.some((i) => i.description?.value))

type UiCard = {
  key: string
  title: string | None
  areas: {
    key: string
    name: string | None
    title: string | None
    value: string | None
    unit: string | None
  }[]
  instructions?: string | None
  measurments?: string | None
}

const tableAll = computed((): UiCard[] => {
  const { values, areas, sizes } = sizeGuide.value || {}
  const unit = unitKey.value

  const res: UiCard[] = []

  const hasDescription = howToMeasure.value
  const instructions = sizeGuide.value?.instructionsNote?.value
  const measurments = sizeGuide.value?.measurmentsNote?.value

  if (hasDescription || instructions || measurments) {
    res.push({
      title: t2('How to measure?'),
      key: KEY_HOW_TO_MEASURE,
      instructions,
      measurments,
      areas: hasDescription
        ? helpers.arr(areas, (item) => {
            return {
              key: item.key,
              name: item.name,
              title: item.title?.value,
              value: item.description?.value,
              unit: undefined,
            }
          })
        : [],
    })
  }

  const unitTitle = helpers.arrFindId(sizeGuide.value?.units, unit, (i) => i.key)?.unit

  sizes?.forEach((size) => {
    const valuesSize = size ? helpers.arr(values).filter((i) => i.sizeKey === size.key && i.unitKey === unit) : []
    res.push({
      title: size.title?.value,
      key: size.key,
      areas: helpers.arr(areas, (item) => {
        return {
          key: item.key,
          name: item.name,
          unit: unitTitle,
          title: item.title?.value,
          value: helpers.arrFindId(valuesSize, item.key, (i) => i.areaKey)?.value,
        }
      }),
    })
  })

  return res || []
})

const carousel = useTemplateRef('carousel')
function scrollTo(key: string) {
  data.activeSize = key
  if (key) carousel.value?.scroll({ top: document.getElementById(key)?.offsetTop, behavior: 'smooth' })
}

const timer = useTimeout()
function scroll() {
  timer.set(() => {
    const scrollTop = carousel.value?.scrollTop || 0
    const offsetHeight = carousel.value?.offsetHeight || 0
    const last = (carousel.value?.scrollHeight || 0) - offsetHeight - 40

    if (scrollTop > last) {
      const lastKey = tableAll.value.at(-1)?.key
      if (lastKey) {
        data.activeSize = lastKey
        return
      }
    }

    let active = { offsetTop: -1, key: '' }

    for (const item of tableAll.value) {
      const elem = document.getElementById(item.key)
      if (elem) {
        const offsetTop = elem?.offsetTop + 40
        if (offsetTop >= scrollTop) {
          active = { offsetTop, key: item.key }
          break
        }
      }
    }

    if (active.key) data.activeSize = active.key
  }, 0)
}

watch(
  () => tableAll.value,
  () => scroll(),
  { immediate: true }
)
</script>

<template>
  <div :class="$style.split">
    <div :class="$style.image1">
      <div v-if="guideBodyId && guideProductId" :class="$style.types" class="_fs08">
        <ButtonInline grey :open="data.state === 'product'" :txt="t2('Product size')" @click="data.state = 'product'" />
        <ButtonInline grey :open="data.state === 'body'" :txt="t2('Body size')" @click="data.state = 'body'" />
      </div>
      <BaseImage :img="sizeGuide?.image" sizes="420px" fit="fit" :no-color="true" class="_pt2 _pb4" />
    </div>

    <div :class="$style.splitB" class="_relative _glass bg">
      <div class="_row _pl04 _pt04 _pr1 _pb1">
        <div :class="$style.options2" class="_row _wrap">
          <SizeGuide4Btn v-for="item in sizeGuide?.sizes" :key="item.key" :txt="item.title?.value" :on="item.key === data.activeSize" @click="scrollTo(item.key)" />
        </div>

        <SizeGuide4Btn v-if="howToMeasure" :on="KEY_HOW_TO_MEASURE === data.activeSize" :txt="t2('How to measure?')" @click="scrollTo(KEY_HOW_TO_MEASURE)" />
      </div>

      <SizeGuide4Units :units="sizeGuide?.units" :model-value="unitKey" @update:model-value="(i) => (data.activeUnit = i)" />

      <div :class="$style.carousel0">
        <div ref="carousel" :class="$style.carousel" @scroll.passive="() => scroll()">
          <SizeGuide4Card
            v-for="table2 in tableAll"
            :id="table2.key"
            :key="table2.key"
            :class="$style.carousel1"
            :on="data.activeSize === table2.key"
            :title="table2.title"
            :measurments="table2.measurments"
            :instructions="table2.instructions"
            :areas="table2.areas"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style module>
.split {
  /* composes: global(_w70ch); */
  composes: global(_bg0);
  composes: global(_grid);

  width: 70vw;

  grid-template: max-content / repeat(2, minmax(0, 1fr));
}
.types {
  composes: global(_flexblock);

  composes: global(_absolute);
  composes: global(_pt08);
  composes: global(_pl08);

  composes: global(_br99);
  composes: global(_gap1);

  top: 0;
  background: #ffffffd6;
  z-index: 1;
  left: 0;
}
.splitB {
  composes: global(_col);

  border: 1px solid var(--blue3);
  border-width: 0 0 0 1px;
  height: 80vh;
}
.options2 {
  gap: 4px 0;
  composes: global(_flex);
}
.image1 {
  composes: global(_ctr);
  height: 80vh;
}

.carousel0 {
  min-height: 0;
  /* margin-left: -1px; */
}
.carousel {
  composes: global(_flexblock);
  composes: global(_relative);
  composes: global(_h100p);
  composes: global(_w100p);
  -webkit-font-smoothing: antialiased;
  scroll-behavior: smooth;
  scroll-padding: 0;
  scroll-snap-type: y mandatory;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -ms-overflow-style: none;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  transform: translateZ(0);
  backface-visibility: hidden;
  touch-action: pan-y pan-x pinch-zoom;
  contain: content;
  overflow: hidden scroll;
  flex-direction: column;
  border-top: 1px solid var(--blue3);
  padding-bottom: 40vh;
  overscroll-behavior: contain;
}
.carousel::-webkit-scrollbar {
  display: none;
}
.carousel1 {
  composes: global(_w100p);
  composes: global(_mt-1);
  scroll-snap-align: start;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  contain: layout style paint;
  border-color: var(--blue3);
  composes: global(_bw10);
}
</style>
